<template>
  <form class="flex flex-col gap-4" novalidate @submit.prevent="() => onFormSubmit()">
    <div class="flex flex-col gap-1.5">
      <ui-label for="password-reset-email">{{ tr('E-mail') }}</ui-label>
      <ui-input
        id="password-reset-email"
        :model-value="email"
        type="email"
        autocomplete="username"
        disabled
      />
    </div>
    <div class="flex flex-col gap-1.5">
      <ui-label for="password-reset-new">{{ tr('New password') }}</ui-label>
      <password-field
        id="password-reset-new"
        v-model="newPassword"
        autocomplete="new-password"
        :placeholder="tr('Enter at least 8 characters...')"
      />
    </div>
    <div class="flex flex-col gap-1.5">
      <ui-label for="password-reset-again">{{ tr('New password again') }}</ui-label>
      <password-field
        id="password-reset-again"
        v-model="newPasswordAgain"
        autocomplete="new-password"
        :placeholder="tr('Repeat your password')"
      />
    </div>

    <ui-button
      type="submit"
      size="lg"
      class="mt-2 w-full"
      :disabled="isLoading"
    >
      <loader-circle-icon v-if="isLoading" class="animate-spin" />
      {{ tr('Change') }}
    </ui-button>
  </form>
</template>

<script>
import { LoaderCircle } from '@lucide/vue';

import { Login2faNecessaryError } from '@/errors';
import AuthService from '@/services/Auth.js';
import Google2faCodeForm from '@/ui/components/Google2faCodeForm.vue';
import PasswordField from '@/ui/components/PasswordField.vue';

export default {
  name: 'PasswordResetView',
  components: {
    LoaderCircleIcon: LoaderCircle,
    PasswordField,
  },
  data() {
    return {
      isLoading: false,
      newPassword: '',
      newPasswordAgain: '',
      code: '',
    };
  },
  computed: {
    token() {
      return this.$route.params.token;
    },
    email() {
      if (!this.$route.query.email) {
        return null;
      }

      return this.$route.query.email.replaceAll(' ', '+');
    },
  },
  methods: {
    resetForm() {
      this.newPassword = '';
      this.newPasswordAgain = '';
      this.code = '';
    },
    open2faFullscreenModal() {
      if (this.fullscreenModalInstance) {
        this.fullscreenModalInstance.close();
      }

      this.fullscreenModalInstance = this.$fullscreenModal.open(
        this,
        Google2faCodeForm,
        () => ({
          modelValue: this.code,
          title: this.tr('Two-factor authentication'),
          trustOption: true,
          isLoading: this.isLoading,
        }),
        {
          componentListeners: {
            submit: ({ trust }) => this.onFormSubmit(trust),
            cancel: () => this.$fullscreenModal.closeAll(),
            'update:modelValue': newCode => this.code = newCode,
          },
        },
      );
    },
    onFormSubmit(trust = false) {
      if (!this.validate()) {
        return;
      }

      const data = { email: this.email, password: this.newPassword };
      if (this.code) {
        data.code = this.code;
        data.trust = trust;
      }

      this.isLoading = true;
      AuthService.resetPassword(this.token, data)
        .then(resp => {
          if ('2fa' in resp) {
            throw new Login2faNecessaryError();
          }

          this.$toast.success(this.tr('Password is modified successfully.'));

          this.$store.commit('auth/setToken', resp.access_token);
          this.$store.commit('auth/setUser', resp.user);

          this.$router.replace({ path: '/dashboard' });

          if (window.PasswordCredential) {
            const passwordCredential = new window.PasswordCredential({ id: this.email, password: this.newPassword });
            navigator.credentials.store(passwordCredential);
          }

          this.resetForm();
          this.$fullscreenModal.closeAll();

          return resp;
        })
        .catch(err => {
          if (err instanceof Login2faNecessaryError) {
            this.open2faFullscreenModal();
          }
          else {
            this.error(err);
          }
        })
        .finally(() => this.isLoading = false);
    },
    validate() {
      this.$toast.clear();

      if (!this.newPassword) {
        this.$toast.error(this.tr('Password cannot be empty!'));
        return false;
      }
      if (this.newPassword.length < 8) {
        this.$toast.error(this.tr('The password must be at least 8 characters long.'));
        return false;
      }
      if (this.newPassword !== this.newPasswordAgain) {
        this.$toast.error(this.tr('Passwords given do not match!'));
        return false;
      }

      return true;
    },
  },
};
</script>
